package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.data.dto.FollowUserPageDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.overlay.FollowListMode
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlRegular
import com.arena_web.ui.theme.h4
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput

private val FieldBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Mobile Following / Followers list — the phone counterpart to the desktop
 * [com.arena_web.ui.components.overlay.FollowListDialog]. Same paging/search
 * model, re-laid-out as a [BottomSheet] with the rich empty states from Figma
 * 10272:224294 (following empty) / 10272:224974 (followers empty).
 */
@Composable
fun MobileFollowListSheet(
    mode: FollowListMode,
    initialCount: Long,
    loadPage: suspend (page: Int, search: String?) -> Result<FollowUserPageDto>,
    onUserClick: (FollowUserDto) -> Unit,
    onDismiss: () -> Unit,
    unfollowedInSessionIds: Set<String> = emptySet(),
    deletedFollowerIds: Set<String> = emptySet(),
    /** The row whose confirm sheet is currently open — its action pill is shown
     *  filled (blue) for the duration. */
    activeActionUserId: String? = null,
    onUnfollowRequest: (FollowUserDto) -> Unit = {},
    onRefollow: (FollowUserDto) -> Unit = {},
    onRemoveRequest: (FollowUserDto) -> Unit = {},
    onDiscoverPlayers: () -> Unit = {},
    /** Another user's lists — no per-row action pills (you can't unfollow on
     *  their behalf or remove their followers). */
    readOnly: Boolean = false,
) {
    val t = LocalAppStrings.current
    var users by remember { mutableStateOf<List<FollowUserDto>>(emptyList()) }
    var count by remember { mutableStateOf(initialCount) }
    var currentPage by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(true) }
    var isLoading by remember { mutableStateOf(true) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    LaunchedEffect(searchQuery) {
        delay(300)
        isLoading = true
        users = emptyList()
        currentPage = 0
        hasMore = true
        loadPage(1, searchQuery.takeIf { it.isNotBlank() })
            .onSuccess { page ->
                users = page.items
                count = page.totalItems
                currentPage = page.page
                hasMore = page.page < page.totalPages
            }
            .onFailure { hasMore = false }
        isLoading = false
    }

    val loadMore: () -> Unit = loadMore@{
        if (isLoading || isLoadingMore || !hasMore) return@loadMore
        isLoadingMore = true
        val nextPage = currentPage + 1
        val activeQuery = searchQuery
        scope.launch {
            loadPage(nextPage, activeQuery.takeIf { it.isNotBlank() })
                .onSuccess { page ->
                    if (activeQuery != searchQuery) return@onSuccess
                    val existing = users.mapTo(mutableSetOf()) { it.userId }
                    users = users + page.items.filter { it.userId !in existing }
                    currentPage = page.page
                    hasMore = page.page < page.totalPages
                    count = page.totalItems
                }
                .onFailure { hasMore = false }
            isLoadingMore = false
        }
    }

    BottomSheet(onDismiss = onDismiss) {
        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(16)) }) {
            // Search.
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("height", s(44))
                    property("border-radius", s(28))
                    property("padding", "0 ${s(8)} 0 ${s(12)}")
                    property("gap", s(6))
                    property("border", "0.5px solid transparent")
                    property("background", FieldBackground)
                    property("box-sizing", "border-box")
                },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier.sSize(24).styleModifier {
                        property("flex-shrink", "0")
                        property("background-color", "#818D9C")
                        property("mask", "url('${AppIcons.Search}') no-repeat center / contain")
                        property("-webkit-mask", "url('${AppIcons.Search}') no-repeat center / contain")
                    }
                )
                TextInput(
                    value = searchQuery,
                    attrs = Modifier
                        .fillMaxWidth()
                        .bodyXlRegular()
                        .color(AppColors.Text.Primary)
                        .styleModifier {
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("min-width", "0")
                            property("font-family", "Urbanist, sans-serif")
                        }
                        .toAttrs {
                            attr("placeholder", t.commonSearch)
                            onInput { e -> searchQuery = e.target.asDynamic().value as String }
                        }
                )
            }

            // Header — title + count badge with a hairline underline.
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("gap", s(8))
                        property("padding-bottom", s(12))
                        property("border-bottom", "1px solid rgba(190, 214, 245, 0.1)")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (mode == FollowListMode.Followers) t.profileFollowListFollowers else t.profileFollowListFollowing,
                    modifier = Modifier.h4().color(AppColors.Text.Secondary)
                )
                Box(
                    modifier = Modifier
                        .sSize(20)
                        .sBorderRadius(100)
                        .backgroundColor(Color("#0061FF")),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        count.toString(),
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(600)
                            .color(AppColors.PrimaryWhite)
                            .styleModifier { property("line-height", "1") }
                    )
                }
            }

            when {
                isLoading -> Box(
                    modifier = Modifier.fillMaxWidth().sPadding(40, 0),
                    contentAlignment = Alignment.Center
                ) { InlineSpinner(size = 24.px) }

                users.isEmpty() && searchQuery.isNotBlank() -> Box(
                    modifier = Modifier.fillMaxWidth().sPadding(40, 0),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(t.profileFollowListNoResults, modifier = Modifier.bodyMRegular().color(AppColors.Text.Muted))
                }

                users.isEmpty() && mode == FollowListMode.Following ->
                    FollowingEmptyState(onDiscoverPlayers = onDiscoverPlayers)

                users.isEmpty() ->
                    FollowersEmptyState()

                else -> Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("max-height", "56vh")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                            property("gap", s(20))
                        }
                        .attrsModifier {
                            onScroll { evt ->
                                if (hasMore && !isLoading && !isLoadingMore) {
                                    val el = evt.target.unsafeCast<org.w3c.dom.HTMLElement>()
                                    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120.0) loadMore()
                                }
                            }
                        }
                ) {
                    users.forEach { user ->
                        FollowRow(
                            user = user,
                            onUserClick = { onUserClick(user) },
                            action = {
                                if (readOnly) {
                                    // no per-row action on another user's lists
                                } else if (mode == FollowListMode.Following) {
                                    val following = user.userId !in unfollowedInSessionIds
                                    val active = user.userId == activeActionUserId
                                    ActionPill(
                                        // "Unfollow" highlights blue while its confirm sheet is
                                        // open; "Follow" (re-follow) is the primary CTA so it
                                        // stays filled.
                                        label = if (following) t.profileFollowListUnfollow else t.profileFollowListFollow,
                                        filled = if (following) active else true,
                                        onClick = {
                                            if (following) onUnfollowRequest(user) else onRefollow(user)
                                        }
                                    )
                                } else {
                                    val removed = user.userId in deletedFollowerIds
                                    val active = user.userId == activeActionUserId
                                    ActionPill(
                                        label = if (removed) t.profileFollowListRemoved else t.profileFollowListRemove,
                                        filled = active,
                                        enabled = !removed,
                                        onClick = { if (!removed) onRemoveRequest(user) }
                                    )
                                }
                            }
                        )
                    }
                    if (isLoadingMore) {
                        Box(
                            modifier = Modifier.fillMaxWidth().sPadding(8, 0),
                            contentAlignment = Alignment.Center
                        ) { InlineSpinner(size = 20.px) }
                    }
                }
            }
        }
    }
}

// ── Row ──

@Composable
private fun FollowRow(
    user: FollowUserDto,
    onUserClick: () -> Unit,
    action: @Composable () -> Unit,
) {
    // Flat flex row: avatar (fixed) + name column (grows, shrinks, min-width:0
    // so long names ellipsis) + action button (fixed). The whole row navigates;
    // the trailing button stops propagation so its own tap fires alone.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onUserClick() }
            .styleModifier { property("gap", s(8)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        FollowAvatar(user)
        Column(
            modifier = Modifier.flexGrow(1).styleModifier { property("min-width", "0") }
        ) {
            // Display name only (no username line), ellipsised so long names
            // never slide under the action button.
            SpanText(
                user.fullName?.takeIf { it.isNotBlank() } ?: user.username,
                modifier = Modifier
                    .fillMaxWidth()
                    .bodyXlRegular()
                    .fontWeight(500)
                    .color(AppColors.Text.Secondary)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .overflow(Overflow.Hidden)
                    .textOverflow(TextOverflow.Ellipsis)
                    .styleModifier { property("display", "block") }
            )
        }
        Box(
            modifier = Modifier.flexShrink(0).onClick { it.stopPropagation() },
            contentAlignment = Alignment.Center
        ) {
            action()
        }
    }
}

@Composable
private fun FollowAvatar(user: FollowUserDto) {
    val cardFrameUrl = user.cardImageUrl
    if (cardFrameUrl != null) {
        // Wrapper dims use m() (driven by --mobile-scale) so they're immune to
        // the local --scale override below. The card's --scale is derived from
        // --mobile-scale directly — NOT `var(--scale)`, which would be a self-
        // referencing cycle here and collapse the card.
        Box(
            modifier = Modifier.flexShrink(0).styleModifier {
                property("width", m(29))
                property("height", m(44))
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("--scale", "calc(var(--mobile-scale) * 0.5)")
            },
            contentAlignment = Alignment.Center
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.XtraSmall,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        }
    } else {
        val avatarUrl = user.avatarUrl
        Box(
            modifier = Modifier
                .sWidth(40)
                .sHeight(40)
                .sBorderRadius(100)
                .styleModifier {
                    property("background", "rgba(60, 121, 241, 0.15)")
                    property("overflow", "hidden")
                    property("flex-shrink", "0")
                },
            contentAlignment = Alignment.Center
        ) {
            if (avatarUrl != null) {
                Image(src = avatarUrl, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
            } else {
                SpanText(
                    user.username.take(1).uppercase(),
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

@Composable
private fun ActionPill(label: String, filled: Boolean, enabled: Boolean = true, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .flexShrink(0)
            .styleModifier {
                property("height", s(40))
                property("padding", "0 ${s(22)}")
                property("border-radius", s(100))
                property("opacity", if (enabled) "1" else "0.5")
                property("box-sizing", "border-box")
                if (filled) {
                    property("background", "#0061ff")
                } else {
                    property("border", "0.3px solid transparent")
                    property("background", FieldBackground)
                }
            }
            .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(if (filled) AppColors.PrimaryWhite else AppColors.Text.Secondary)
                .whiteSpace(WhiteSpace.NoWrap)
        )
    }
}

// ── Empty states ──

@Composable
private fun FollowingEmptyState(onDiscoverPlayers: () -> Unit) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPadding(8, 0).styleModifier { property("gap", s(28)) },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Person + add-badge illustration (Figma 10272:224635). The "+" is a
        // small badge above the person's right shoulder, not a big overlapping
        // glyph — geometry mirrors the design: person glyph 50.7×54.6 nudged
        // down 10.8, "+" glyph 19.5 at the top-right.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "relative")
                property("width", s(66.3))
                property("height", s(65.4))
            }
        ) {
            Image(
                src = AppIcons.EmptyFollowingPerson,
                modifier = Modifier.sWidth(50.7).sHeight(54.6).styleModifier {
                    property("position", "absolute")
                    property("left", "0")
                    property("top", s(10.8))
                }
            )
            Image(
                src = AppIcons.EmptyFollowingAdd,
                modifier = Modifier.sSize(19.5).styleModifier {
                    property("position", "absolute")
                    property("left", s(46.8))
                    property("top", "0")
                }
            )
        }

        Column(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(8)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(t.profileFollowListNotFollowingTitle, modifier = Modifier.h4().color(AppColors.Text.Secondary))
            SpanText(
                t.profileFollowListNotFollowingBody,
                modifier = Modifier
                    .fillMaxWidth()
                    .bodyMRegular()
                    .color(Color("#99B1BF"))
                    .textAlign(TextAlign.Center)
                    .styleModifier { property("line-height", s(22)) }
            )
        }

        // Discover Players button.
        Row(
            modifier = Modifier
                .styleModifier {
                    property("height", s(46))
                    property("border-radius", s(100))
                    property("padding", "0 ${s(4)} 0 ${s(32)}")
                    property("gap", s(10))
                    property("background", "#0061ff")
                }
                .cursor(Cursor.Pointer)
                .onClick { onDiscoverPlayers() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(t.profileFollowListDiscoverPlayers, modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite))
            Box(
                modifier = Modifier
                    .sSize(38)
                    .sBorderRadius(100)
                    .backgroundColor(Color("#004DCD")),
                contentAlignment = Alignment.Center
            ) {
                Image(
                    src = AppIcons.DiscoverArrow,
                    modifier = Modifier.sSize(16).styleModifier { property("transform", "rotate(45deg)") }
                )
            }
        }
    }
}

@Composable
private fun FollowersEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPadding(16, 0).styleModifier { property("gap", s(22)) },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Image(src = AppIcons.EmptyFollowers, modifier = Modifier.sWidth(76).sHeight(56))
        Column(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(8)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(t.profileFollowListNoFollowersTitle, modifier = Modifier.h4().color(AppColors.Text.Secondary))
            SpanText(
                t.profileFollowListNoFollowersBody,
                modifier = Modifier
                    .fillMaxWidth()
                    .bodyMRegular()
                    .color(Color("#99B1BF"))
                    .textAlign(TextAlign.Center)
                    .styleModifier { property("line-height", s(22)) }
            )
        }
    }
}
